import { memo } from 'react'

import { positionReading } from '../../model/portfolio-readings'

import {
  count,
  percent,
  quantity,
  shortAddress,
  signedUsd,
  since,
  usd,
} from './money'
import { TokenMark } from './token-mark'

import type { Holding } from '../../model/portfolio-readings'
import type { Trade } from '@root/entities/trade'
import type { WalletProfile } from '@root/features/services/wallet-profile'

/**
 * Every token this account has traded, best first.
 *
 * The server sorts by profit and sends fifty, which is the list a trader
 * actually reads. A position whose cost basis was never seen shows a dash
 * where its profit would be: the tokens arrived, the buys did not, and a zero
 * there would read as break-even.
 */
export const PositionsList: React.FC<{
  positions: ReadonlyArray<WalletProfile.Position>
  logoHost: (logo: string) => string | undefined
}> = memo(({ positions, logoHost }) => {
  if (positions.length === 0) {
    return (
      <p className="blank">
        Nothing traded on these wallets yet. A position appears here the moment
        its first buy is indexed.
      </p>
    )
  }

  return (
    <div className="scroll">
      <table className="tb2">
        <thead>
          <tr>
            <th>Token</th>
            <th>Spent</th>
            <th>Received</th>
            <th>Holding</th>
            <th>Profit</th>
            <th>Entry cap</th>
            <th>Last trade</th>
          </tr>
        </thead>
        <tbody>
          {positions.map((position) => {
            const reading = positionReading(position)

            return (
              <tr key={`${position.token}:${position.pairAddress}`}>
                <td>
                  <span className="tok">
                    <TokenMark
                      src={logoHost(position.logo)}
                      ticker={position.symbol}
                    />
                    <span className="txt">
                      <span className="tk">
                        {position.symbol || shortAddress(position.token)}
                      </span>
                      <span className="nm2">
                        {position.name || shortAddress(position.token, 6)}
                      </span>
                    </span>
                    <span className="tags">
                      {reading.open ? (
                        <span className="tag">Open</span>
                      ) : undefined}
                      {position.costBasisKnown ? undefined : (
                        <span
                          className="tag"
                          title="The buys for this token were never seen, so its profit cannot be worked out."
                        >
                          No basis
                        </span>
                      )}
                    </span>
                  </span>
                </td>
                <td>{usd(position.spentUsd)}</td>
                <td>{usd(position.receivedUsd)}</td>
                <td>
                  {position.remainingTokens > 0
                    ? quantity(position.remainingTokens)
                    : '—'}
                </td>
                <td className={toneOf(reading.tone)}>
                  {reading.pnl === undefined ? (
                    '—'
                  ) : (
                    <>
                      {signedUsd(reading.pnl)}
                      {reading.pnlPercent === undefined ? undefined : (
                        <i
                          style={{
                            fontStyle: 'normal',
                            color: 'var(--t4)',
                            marginLeft: 6,
                          }}
                        >
                          {percent(reading.pnlPercent, { sign: true })}
                        </i>
                      )}
                    </>
                  )}
                </td>
                <td>
                  {position.avgBuyMarketCap === null
                    ? '—'
                    : usd(position.avgBuyMarketCap)}
                </td>
                <td>{since(position.lastTradeAt)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
})

/**
 * What is held right now, across every wallet at once.
 *
 * Nothing in the app merged the wallets before: the wallet manager shows six
 * lists of assets, one per wallet, and a trader with one token spread over
 * three of them had to add it up by eye. This is that sum, and it keeps the
 * number of wallets, because a holding split three ways is a different thing
 * to sell.
 */
export const HoldingsList: React.FC<{
  holdings: ReadonlyArray<Holding>
}> = memo(({ holdings }) => {
  if (holdings.length === 0) {
    return (
      <p className="blank">
        These wallets hold nothing yet. Deposit, and it shows up here as soon as
        the balance is read.
      </p>
    )
  }

  return (
    <div className="scroll">
      <table className="tb2">
        <thead>
          <tr>
            <th>Token</th>
            <th>Amount</th>
            <th>Price</th>
            <th>Value</th>
            <th>Share</th>
            <th>Wallets</th>
          </tr>
        </thead>
        <tbody>
          {holdings.map((holding) => (
            <tr key={holding.address}>
              <td>
                <span className="tok">
                  <TokenMark src={holding.icon} ticker={holding.ticker} />
                  <span className="txt">
                    <span className="tk">{holding.ticker}</span>
                    <span className="nm2">{holding.title}</span>
                  </span>
                </span>
              </td>
              <td>{quantity(holding.amount)}</td>
              <td>{usd(holding.priceUsd)}</td>
              <td style={{ color: 'var(--t1)' }}>{usd(holding.valueUsd)}</td>
              <td>{percent(holding.share)}</td>
              <td>{holding.wallets}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
})

/**
 * What these wallets did, most recent first.
 *
 * The same read the trade panel's tape uses, asked about every wallet on the
 * account instead of one pair. Fifty is a page and there is no more: somebody
 * reading their own history wants this morning, and a month of it belongs in
 * an export rather than a scroll.
 */
export const HistoryList: React.FC<{
  trades: ReadonlyArray<Trade.Trade>
  loading: boolean
}> = memo(({ trades, loading }) => {
  if (trades.length === 0) {
    return (
      <p className="blank">
        {loading
          ? 'Reading the last trades…'
          : 'No trades on these wallets yet.'}
      </p>
    )
  }

  return (
    <div className="scroll">
      <table className="tb2">
        <thead>
          <tr>
            <th>Token</th>
            <th>Side</th>
            <th>Amount</th>
            <th>Total</th>
            <th>Market cap</th>
            <th>Wallet</th>
            <th>Age</th>
          </tr>
        </thead>
        <tbody>
          {trades.map((trade) => {
            return (
              <tr key={trade.id}>
                <td>
                  <span className="tok">
                    <TokenMark
                      src={trade.quoteCurrency?.icon.toString()}
                      ticker={trade.quoteCurrency?.ticker ?? '?'}
                    />
                    <span className="txt">
                      <span className="tk">
                        {trade.quoteCurrency?.ticker ??
                          shortAddress(trade.quoteCurrencyAddress)}
                      </span>
                      <span className="nm2">
                        {trade.quoteCurrency?.title ?? ''}
                      </span>
                    </span>
                  </span>
                </td>
                <td className={sideTone(trade.type)}>{sideOf(trade.type)}</td>
                <td>{quantity(trade.currencyAmount.toNumber())}</td>
                <td style={{ color: 'var(--t1)' }}>
                  {usd(trade.totalUsd.toNumber())}
                </td>
                <td>{usd(trade.marketCapUsd.toNumber())}</td>
                <td>{shortAddress(trade.maker.address)}</td>
                <td>{since(trade.timestamp.getTime())}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
})

/**
 * The wallets themselves.
 *
 * Cards rather than a table, because a wallet is a thing you act on — copy it,
 * fund it, move out of it — and a row of icon buttons at the end of a wide
 * table is where those actions go to be missed. The share bar is the wallet's
 * part of everything held, which is the one figure the wallet manager never
 * showed.
 */
export const WalletsList: React.FC<{
  wallets: ReadonlyArray<{
    id: string
    name: string
    address: string
    colour: string
    usd: number
    native: number
    assets: number
    share: number
    exportable: boolean
  }>
  onCopy: (address: string) => void
  onDeposit: (id: string) => void
  onTransfer: (id: string) => void
  /** Hands the key back — only a wallet whose key we hold has one to give. */
  onExport: (id: string, name: string) => void
  onManage: () => void
}> = memo(({ wallets, onCopy, onDeposit, onTransfer, onExport, onManage }) => {
  if (wallets.length === 0) {
    return (
      <p className="blank">
        No wallets on this account yet.{' '}
        <button className="btn" type="button" onClick={onManage}>
          Create one
        </button>
      </p>
    )
  }

  return (
    <div className="wcards">
      {wallets.map((wallet) => (
        <article className="wc" key={wallet.id}>
          <div className="top2">
            <span className="swatch" style={{ background: wallet.colour }}>
              {wallet.name.slice(0, 1).toUpperCase()}
            </span>
            <span className="who">
              <b>{wallet.name}</b>
              <span>{shortAddress(wallet.address, 5)}</span>
            </span>
            <span className="tags" style={{ marginLeft: 'auto' }}>
              <span className="tag">{count(wallet.assets)} tokens</span>
            </span>
          </div>

          <div className="amt">
            <b>{usd(wallet.usd)}</b>
            <span>{wallet.native} SOL</span>
          </div>

          <span className="share">
            <i style={{ width: `${Math.min(wallet.share, 100)}%` }} />
          </span>

          <div className="acts">
            <button
              className="btn"
              type="button"
              onClick={() => onCopy(wallet.address)}
            >
              Copy
            </button>
            <button
              className="btn"
              type="button"
              onClick={() => onDeposit(wallet.id)}
            >
              Deposit
            </button>
            <button
              className="btn"
              type="button"
              onClick={() => onTransfer(wallet.id)}
            >
              Send
            </button>
            {wallet.exportable ? (
              <button
                className="btn"
                type="button"
                title="Hand back this wallet's private key"
                onClick={() => onExport(wallet.id, wallet.name)}
              >
                Export
              </button>
            ) : (
              <span
                className="tag"
                title="Its key is on the machine that made it"
              >
                On a device
              </span>
            )}
          </div>
        </article>
      ))}
    </div>
  )
})

const toneOf = (tone: 'quiet' | 'good' | 'bad') => {
  if (tone === 'good') {
    return 'good'
  }
  return tone === 'bad' ? 'bad' : undefined
}

/** Buy, sell, or whatever else the indexer called it. */
const sideOf = (type: string) => {
  if (type === 'buy') {
    return 'Buy'
  }
  return type === 'sell' ? 'Sell' : type
}

const sideTone = (type: string) => {
  if (type === 'buy') {
    return 'good'
  }
  return type === 'sell' ? 'bad' : undefined
}
